import _ from 'lodash';
import React, {FunctionComponent, memo, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Grid, makeStyles, IconButton, Tooltip} from '@material-ui/core';
import {MergeType} from '@material-ui/icons';
import {OwnerType, ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {Button, ButtonClass} from '@treez-ui/core/buttons';
import {Checkbox} from '@treez-ui/core/forms';
import {CloseIcon} from '@treez-ui/core/icons';
import {removeProductSourceFromAll, setAllProductSources} from '~/redux/actions/MergeActions';
import {treezColorMap, TreezColor} from '~/styles/constants';
import {selectMergeProduct, selectProductFieldSources} from '~/redux/selectors/mergeSelectors';

const {GREY_LIGHT, OFF_WHITE, ERROR_RED} = TreezColor;

interface Props {
    mergeToThisProduct: (existingProduct: ProductInCatalog) => void;
    ownerType: OwnerType;
    product: ProductInCatalog;
    removeProduct?: (existingProductId: number) => void;
}

export const ProductOverviewCell: FunctionComponent<Props> = (props: Props) => {
    const {product, mergeToThisProduct, ownerType, removeProduct} = props;

    const productToMerge = useSelector(selectMergeProduct);
    const productFieldSources = useSelector(selectProductFieldSources);

    const valuesMatchMergeToProduct = useMemo(() => {
        if (_.isEmpty(productFieldSources)) {
            return false;
        }
        return Object.keys(productToMerge).every((field) => productFieldSources[field] === product);
    }, [productFieldSources]);

    const dispatch = useDispatch();

    const handleSelectAll = (selected: boolean) => {
        if (selected) {
            dispatch(setAllProductSources(product));
        } else {
            dispatch(removeProductSourceFromAll(product));
        }
    };

    const useStyles = makeStyles({
        optionsContainer: {
            padding: '0 16px',
        },
        overviewCell: {
            backgroundColor: treezColorMap[OFF_WHITE],
            borderBottom: `1px solid ${treezColorMap[GREY_LIGHT]}`,
            boxSizing: 'border-box',
            display: 'flex',
            flexWrap: 'wrap',
            height: '110px',
        },
        selectAllValuesContainer: {
            display: 'flex',
            alignItems: 'center',
            height: '30px',
        },
        mergeToThisButtonContainer: {
            display: 'flex',
            width: '180px',
            height: '50px',
        },
    });

    const {optionsContainer, overviewCell, selectAllValuesContainer, mergeToThisButtonContainer} =
        useStyles();

    const renderMergeToThisProductButton = () => {
        const renderButton = (disabled: boolean) => (
            <div>
                <Button
                    disabled={disabled}
                    buttonClass={ButtonClass.PREFERRED}
                    icon={<MergeType />}
                    onClick={() => mergeToThisProduct(product)}
                    text="Merge To This Product"
                />
            </div>
        );

        if (ownerType === OwnerType.ORG) {
            return renderButton(false);
        }
        return (
            <Tooltip
                title={
                    <div className="tool-tip">
                        Only products owned by the organization can be shared across stores. Store
                        owned products can only be assigned to its individual store.
                    </div>
                }
            >
                {renderButton(true)}
            </Tooltip>
        );
    };

    return (
        <Grid className={overviewCell}>
            <Grid
                item
                container
                justifyContent="space-between"
                alignItems="center"
                sm={12}
                className={optionsContainer}
            >
                <Grid className={selectAllValuesContainer}>
                    <Checkbox
                        checked={valuesMatchMergeToProduct}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
                            handleSelectAll(checked)
                        }
                    />
                    Select All Values
                </Grid>
                <div>
                    <IconButton
                        onClick={() => removeProduct?.(product.id)}
                        disabled={removeProduct == null}
                    >
                        <CloseIcon color={treezColorMap[ERROR_RED]} />
                    </IconButton>
                </div>
            </Grid>
            <Grid sm={12} container item justifyContent="center">
                <Grid className={mergeToThisButtonContainer}>
                    {renderMergeToThisProductButton()}
                </Grid>
            </Grid>
        </Grid>
    );
};

export default memo(ProductOverviewCell);
